import { Alert } from '@mui/joy';
import { AdminQueryError } from '../../../components/admin/AdminQueryError';
import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  LinearProgress,
  Switch,
  Tab,
  TabList,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/joy';
import { Plus, Trash, ArrowsClockwise } from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ResponsiveTable } from '../../../components/common/ResponsiveTable';
import type { ResponsiveColumn } from '../../../components/common/ResponsiveTable';
import { CreateRecordModal } from '../../../components/admin/CreateRecordModal';
import { AdminSequencesAPI } from '../../../lib/apiAdmin';
import { getApiErrorMessage } from '../../../lib/apiError';
import type { OnboardingEnrollment, OnboardingSequence, PlatformEmailTemplate } from '../../../types/admin';
import { SequenceDetailDrawer } from './SequenceDetailDrawer';
import { TemplateEditorDrawer } from './TemplateEditorDrawer';
import { AccountSection } from '../accounts/AccountSection';
import { TemplateFieldGuide, MessagePreview } from './MessagePersonalization';
import { TemplateFolders, TemplateFolderSelect } from './TemplateFolders';
import { useTemplateFolders, templateFoldersKey } from './useTemplateFolders';

type View = 'sequences' | 'templates' | 'enrolled';

const ENROLMENT_COLORS: Record<string, 'primary' | 'success' | 'neutral' | 'warning'> = {
  active: 'primary',
  completed: 'success',
  cancelled: 'neutral',
  suppressed: 'warning',
};

const formatDate = (value: string | null) =>
  value ? new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '—';

/**
 * The drip campaigns Customer Success sends: the sequences themselves, the
 * email bodies they draw on, and who is currently working through one.
 *
 * All three are one screen because they are one job — you cannot build a
 * sequence without a template, and the only way to tell whether a sequence is
 * doing anything is to look at who is on it.
 */
export const AdminSequencesPanel: React.FC = () => {
  const queryClient = useQueryClient();
  const [view, setView] = useState<View>('sequences');
  const [openSequenceId, setOpenSequenceId] = useState<string | null>(null);
  const [openTemplateId, setOpenTemplateId] = useState<string | null>(null);
  const [creating, setCreating] = useState<'sequence' | 'template' | null>(null);
  const [selectedFolder, setSelectedFolder] = useState('all');
  const foldersQuery = useTemplateFolders(view === 'templates' || openTemplateId !== null);
  const [createError, setCreateError] = useState<string | null>(null);

  const sequencesQuery = useQuery({
    queryKey: ['admin', 'sequences'],
    queryFn: () => AdminSequencesAPI.listSequences(),
  });

  const templatesQuery = useQuery({
    queryKey: ['admin', 'email-templates'],
    queryFn: () => AdminSequencesAPI.listTemplates(),
    enabled: view === 'templates' || view === 'sequences',
  });

  const templates = templatesQuery.data?.results ?? [];
  const folderCounts = templates.reduce<Record<string, number>>((counts, template) => {
    const key = template.folder ?? 'unfiled';
    counts[key] = (counts[key] ?? 0) + 1;
    return counts;
  }, { all: templates.length });
  const visibleTemplates = selectedFolder === 'all' ? templates
    : templates.filter(template => (template.folder ?? 'unfiled') === selectedFolder);

  const enrollmentsQuery = useQuery({
    queryKey: ['admin', 'enrollments'],
    queryFn: () => AdminSequencesAPI.listEnrollments({ page_size: 200 }),
    enabled: view === 'enrolled',
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: templateFoldersKey });
    queryClient.invalidateQueries({ queryKey: ['admin', 'sequences'] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'email-templates'] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'enrollments'] });
  };

  const createSequence = useMutation({
    mutationFn: (values: Record<string, string>) =>
      AdminSequencesAPI.createSequence({
        name: values.name,
        from_name: values.from_name || 'ApexOS',
        reply_to: values.reply_to || '',
      }),
    onSuccess: (sequence) => {
      setCreating(null);
      setCreateError(null);
      invalidate();
      setOpenSequenceId(sequence.id);
    },
    onError: (error: unknown) =>
      setCreateError(getApiErrorMessage(error, 'That sequence could not be created.')),
  });

  const createTemplate = useMutation({
    mutationFn: (values: Record<string, string>) => {
      const folder = values.folder || selectedFolder;
      return AdminSequencesAPI.createTemplate({
        name: values.name,
        subject: values.subject,
        body_text: values.body_text,
        body_html: values.body_html || '',
        folder: folder === 'all' || folder === 'unfiled' ? null : folder,
      });
    },
    onSuccess: (template) => {
      setCreating(null);
      setCreateError(null);
      invalidate();
      setOpenTemplateId(template.id);
    },
    onError: (error: unknown) =>
      setCreateError(getApiErrorMessage(error, 'That template could not be created.')),
  });

  const toggleSequence = useMutation({
    mutationFn: ({ id, is_active }: { id: string; is_active: boolean }) =>
      AdminSequencesAPI.updateSequence(id, { is_active }),
    onSuccess: invalidate,
  });

  const deleteTemplate = useMutation({
    mutationFn: (id: string) => AdminSequencesAPI.deleteTemplate(id),
    onSuccess: invalidate,
  });

  const moveTemplate = useMutation({
    mutationFn: ({ id, folder }: { id: string; folder: string | null }) =>
      AdminSequencesAPI.updateTemplate(id, { folder }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'email-templates'] }),
  });

  const cancelEnrollment = useMutation({
    mutationFn: (id: string) => AdminSequencesAPI.cancelEnrollment(id),
    onSuccess: invalidate,
  });

  const sequenceColumns: ResponsiveColumn<OnboardingSequence>[] = [
    {
      key: 'name',
      header: 'Sequence',
      mobileVariant: 'header',
      mobileLabel: 'Sequence',
      sortValue: (row) => row.name,
      render: (row) => (
        <Box sx={{ cursor: 'pointer' }} onClick={() => setOpenSequenceId(row.id)}>
          <Typography level="title-sm">{row.name}</Typography>
          <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
            {row.steps.length} step{row.steps.length === 1 ? '' : 's'}
            {row.auto_enrol ? ' · enrols new signups automatically' : ''}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'enrolled',
      header: 'On it now',
      width: '14%',
      sortValue: (row) => row.enrolled_count,
      render: (row) => <Typography level="body-sm">{row.enrolled_count || '—'}</Typography>,
    },
    {
      key: 'window',
      header: 'Sends between',
      width: '18%',
      render: (row) => (
        <Typography level="body-sm">
          {row.send_after_hour}:00 – {row.send_before_hour}:00
        </Typography>
      ),
    },
    {
      key: 'active',
      header: 'Live',
      width: '12%',
      sortValue: (row) => row.is_active,
      render: (row) => (
        <Tooltip size="sm" variant="soft" title={row.is_active ? 'Sending' : 'Paused — nothing goes out'}>
          <Switch
            size="sm"
            checked={row.is_active}
            onChange={(event) => toggleSequence.mutate({ id: row.id, is_active: event.target.checked })}
          />
        </Tooltip>
      ),
    },
  ];

  const templateColumns: ResponsiveColumn<PlatformEmailTemplate>[] = [
    {
      key: 'name',
      header: 'Template',
      mobileVariant: 'header',
      mobileLabel: 'Template',
      sortValue: (row) => row.name,
      render: (row) => (
        <Box sx={{ cursor: 'pointer' }} onClick={() => setOpenTemplateId(row.id)}>
          <Typography level="title-sm">{row.name}</Typography>
          <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
            {row.subject}
          </Typography>
        </Box>
      ),
    },
    {
      key: 'folder',
      header: 'Folder',
      render: row => <TemplateFolderSelect folders={foldersQuery.data ?? []} value={row.folder ?? null}
        label={`Folder for ${row.name}`} disabled={moveTemplate.isPending || !foldersQuery.data}
        onChange={folder => moveTemplate.mutate({ id: row.id, folder })} />,
    },
    {
      key: 'preview',
      header: 'Opening line',
      mobileVariant: 'body',
      render: (row) => (
        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
          {row.body_text.split('\n').find((line) => line.trim()) ?? '—'}
        </Typography>
      ),
    },
    {
      key: 'updated',
      header: 'Updated',
      width: '14%',
      sortValue: (row) => new Date(row.updated_at),
      render: (row) => <Typography level="body-xs">{formatDate(row.updated_at)}</Typography>,
    },
  ];

  const enrollmentColumns: ResponsiveColumn<OnboardingEnrollment>[] = [
    {
      key: 'customer',
      header: 'Customer',
      mobileVariant: 'header',
      mobileLabel: 'Customer',
      sortValue: (row) => row.customer_name,
      render: (row) => <Typography level="title-sm">{row.customer_name}</Typography>,
    },
    {
      key: 'sequence',
      header: 'Sequence',
      sortValue: (row) => row.sequence_name,
      render: (row) => <Typography level="body-sm">{row.sequence_name}</Typography>,
    },
    {
      key: 'status',
      header: 'Status',
      width: '14%',
      sortValue: (row) => row.status,
      render: (row) => (
        <Chip size="sm" variant="soft" color={ENROLMENT_COLORS[row.status] ?? 'neutral'}>
          {row.status}
        </Chip>
      ),
    },
    {
      key: 'progress',
      header: 'Sent',
      width: '10%',
      render: (row) => <Typography level="body-sm">{row.steps_run}</Typography>,
    },
    {
      key: 'enrolled_at',
      header: 'Enrolled',
      width: '14%',
      sortValue: (row) => new Date(row.enrolled_at),
      render: (row) => <Typography level="body-xs">{formatDate(row.enrolled_at)}</Typography>,
    },
  ];

  const isFetching = sequencesQuery.isFetching || templatesQuery.isFetching || enrollmentsQuery.isFetching;

  return (
    <Box sx={{ display: 'grid', gap: 3 }}>
      <Box>
        <Typography component="h2" level="h3">
          Email sequences
        </Typography>
        <Typography level="body-sm" sx={{ mt: 0.5 }}>
          Build the messages, choose when they run, and see which customers are enrolled.
        </Typography>
      </Box>
      <AdminQueryError
        error={sequencesQuery.error || templatesQuery.error || enrollmentsQuery.error || foldersQuery.error}
        retry={invalidate}
      />
      {(toggleSequence.error || deleteTemplate.error || cancelEnrollment.error || moveTemplate.error) && (
        <Alert color="danger">
          {getApiErrorMessage(
            toggleSequence.error || deleteTemplate.error || cancelEnrollment.error || moveTemplate.error,
            'The update could not be saved.',
          )}
        </Alert>
      )}
      <SequenceDetailDrawer
        sequenceId={openSequenceId}
        templates={templatesQuery.data?.results ?? []}
        onClose={() => setOpenSequenceId(null)}
      />
      <TemplateEditorDrawer templateId={openTemplateId} onClose={() => setOpenTemplateId(null)} />

      <CreateRecordModal
        open={creating === 'sequence'}
        onClose={() => {
          setCreating(null);
          setCreateError(null);
        }}
        title="New sequence"
        submitLabel="Create"
        isSubmitting={createSequence.isPending}
        error={createError}
        fields={[
          { name: 'name', label: 'Name', required: true, placeholder: 'First two weeks' },
          { name: 'from_name', label: 'From name', placeholder: 'ApexOS' },
          { name: 'reply_to', label: 'Reply-to', placeholder: 'success@letsgoapex.com' },
        ]}
        onSubmit={(values) => createSequence.mutate(values)}
      />

      <CreateRecordModal
        key={`template-${creating ?? 'closed'}-${selectedFolder}`}
        open={creating === 'template'}
        onClose={() => {
          setCreating(null);
          setCreateError(null);
        }}
        title="New email template"
        renderDetails={(values) => (
          <>
            <TemplateFieldGuide />
            <MessagePreview
              title={values.name || 'New email template'}
              subject={values.subject || ''}
              body={values.body_text || ''}
              body_html={values.body_html || ''}
            />
          </>
        )}
        submitLabel="Create"
        isSubmitting={createTemplate.isPending}
        error={createError}
        fields={[
          { name: 'name', label: 'Name', required: true, placeholder: 'Day 3 — add your first job' },
          { name: 'folder', label: 'Folder', type: 'select', options: [
            ...(foldersQuery.data ?? []).filter(folder => folder.id === selectedFolder).map(folder => ({ value: folder.id, label: folder.name })),
            { value: 'unfiled', label: 'Unfiled' },
            ...(foldersQuery.data ?? []).filter(folder => folder.id !== selectedFolder).map(folder => ({ value: folder.id, label: folder.name })),
          ] },
          {
            name: 'subject',
            label: 'Subject',
            required: true,
            placeholder: 'A next step for {{company_name}}',
          },
          {
            name: 'body_text',
            label: 'Body',
            type: 'email',
            required: true,
            placeholder: 'Hi {{first_name}},',
          },
        ]}
        onSubmit={(values) => createTemplate.mutate(values)}
      />

      <AccountSection
        title={
          view === 'sequences'
            ? 'Automated follow-ups'
            : view === 'templates'
              ? 'Reusable email templates'
              : 'Customer enrollments'
        }
        description={
          view === 'sequences'
            ? 'A sequence sends a series of emails on a schedule. Open one to edit its steps, templates, and enrollment rules.'
            : view === 'templates'
              ? 'Write each message once, then reuse it in a sequence or an account email. Template variables personalize it for the customer.'
              : 'Track each customer’s progress through a sequence. Stop an active enrollment to prevent further emails.'
        }
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Tabs value={view} onChange={(_, value) => setView(value as View)} sx={{ bgcolor: 'transparent' }}>
            <TabList size="sm" aria-label="Email sequence tools" sx={{ flexWrap: 'wrap' }}>
              <Tab value="sequences">Sequences</Tab>
              <Tab value="templates">Templates</Tab>
              <Tab value="enrolled">Enrolled customers</Tab>
            </TabList>
          </Tabs>
          <Box sx={{ flex: 1 }} />
          {view !== 'enrolled' && (
            <Button
              size="sm"
              variant="outlined"
              startDecorator={<Plus size={16} />}
              onClick={() => setCreating(view === 'sequences' ? 'sequence' : 'template')}
            >
              {view === 'sequences' ? 'New sequence' : 'New template'}
            </Button>
          )}
          <IconButton size="sm" variant="outlined" color="neutral" aria-label="Refresh" onClick={invalidate}>
            <ArrowsClockwise size={16} />
          </IconButton>
        </Box>

        {isFetching && <LinearProgress size="sm" />}

        {view === 'sequences' && (
          <ResponsiveTable<OnboardingSequence>
            rows={sequencesQuery.data?.results ?? []}
            getRowKey={(row) => row.id}
            columns={sequenceColumns}
            pagination={{ pageSize: 25, itemNoun: 'sequences' }}
            emptyState={
              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                No sequences yet. A sequence with "enrol automatically" turned on starts new signups on it the
                day they register.
              </Typography>
            }
          />
        )}

        {view === 'templates' && (
          <>
            <TemplateFolders folders={foldersQuery.data ?? []} selected={selectedFolder}
              onSelect={setSelectedFolder} counts={folderCounts} />
            <ResponsiveTable<PlatformEmailTemplate>
              rows={visibleTemplates}
              getRowKey={(row) => row.id}
              columns={templateColumns}
              pagination={{ pageSize: 25, itemNoun: 'templates' }}
              rowActions={(row) => (
                <Tooltip size="sm" variant="soft" title="Delete template">
                  <IconButton
                    size="sm"
                    variant="plain"
                    color="danger"
                    aria-label="Delete"
                    loading={deleteTemplate.isPending && deleteTemplate.variables === row.id}
                    onClick={() => deleteTemplate.mutate(row.id)}
                  >
                    <Trash size={16} />
                  </IconButton>
                </Tooltip>
              )}
              emptyState={
                <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                  No templates in this view. Create one here or choose another folder.
                </Typography>
              }
            />
          </>
        )}

        {view === 'enrolled' && (
          <ResponsiveTable<OnboardingEnrollment>
            rows={enrollmentsQuery.data?.results ?? []}
            getRowKey={(row) => row.id}
            columns={enrollmentColumns}
            pagination={{ pageSize: 25, itemNoun: 'enrollments' }}
            rowActions={(row) =>
              row.status === 'active' ? (
                <Button
                  size="sm"
                  variant="plain"
                  color="neutral"
                  loading={cancelEnrollment.isPending && cancelEnrollment.variables === row.id}
                  onClick={() => cancelEnrollment.mutate(row.id)}
                >
                  Stop
                </Button>
              ) : null
            }
            emptyState={
              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                Nobody is on a sequence yet.
              </Typography>
            }
          />
        )}
      </AccountSection>
    </Box>
  );
};

export default AdminSequencesPanel;
